/**
 * The admin screens' decisions, pure and table-tested (`admin.test.ts`): what a
 * node's certificate, link and health say at a glance, how a group's scope
 * reads, the usage report's per-production totals, the audit filter, and the
 * month choices. The components only render what these return.
 */
import type { AuditEntry } from '@acp/api-contract/offlineAuth.js';
import type { CloudUsageResponse } from '@acp/api-contract/cloudProductions.js';
import type { SiteNode } from '@acp/api-contract/sites.js';
import type { Group } from '@acp/api-contract/tenancy.js';
import { describeWitness } from '@acp/api-contract/witness.js';

/** A certificate this close to expiry is flagged (Let's Encrypt renews at 60 of 90 days, so 21 days left means a renewal is overdue). */
export const CERT_WARNING_MS = 21 * 24 * 60 * 60 * 1000;

export type CertState = { readonly tone: 'ok' | 'warning' | 'expired' | 'none'; readonly text: string };

export function certState(certExpiresAt: string | null, nowMs: number): CertState {
  if (certExpiresAt === null) return { tone: 'none', text: 'none yet' };
  const left = Date.parse(certExpiresAt) - nowMs;
  const date = certExpiresAt.slice(0, 10);
  if (left <= 0) return { tone: 'expired', text: `expired ${date}` };
  const days = Math.floor(left / 86_400_000);
  return { tone: left < CERT_WARNING_MS ? 'warning' : 'ok', text: `${date} (${String(days)} d)` };
}

/** "12 s ago", "5 min ago", "3 h ago", "2 d ago"; "never". */
export function agoText(iso: string | null, nowMs: number): string {
  if (iso === null) return 'never';
  const s = Math.max(0, Math.round((nowMs - Date.parse(iso)) / 1000));
  if (s < 60) return `${String(s)} s ago`;
  if (s < 3600) return `${String(Math.floor(s / 60))} min ago`;
  if (s < 86_400) return `${String(Math.floor(s / 3600))} h ago`;
  return `${String(Math.floor(s / 86_400))} d ago`;
}

/** The last health report's gist in one line, or null before the first. */
export function healthText(health: SiteNode['health']): string | null {
  if (health === null) return null;
  const pct = (label: string, value: number | null) => (value === null ? null : `${label} ${String(Math.round(value))}%`);
  const parts = [pct('CPU', health.cpuPercent), pct('mem', health.memoryUsedPercent), pct('disk', health.diskUsedPercent)];
  if (health.clockDriftMs !== null) parts.push(`drift ${String(Math.round(health.clockDriftMs))} ms`);
  parts.push(`${String(health.productions)} production${health.productions === 1 ? '' : 's'}`);
  return parts.filter((p): p is string => p !== null).join(' · ');
}

/** Which witness is in force on the node and its expected failover (D30), from its last report; null when it has not said. */
export function witnessText(health: SiteNode['health']): string | null {
  const witness = health?.witness;
  return witness === undefined || witness === null ? null : describeWitness(witness);
}

/** A package hash as people compare them: its first 12 characters. */
export function shortHash(hash: string): string {
  return hash.slice(0, 12);
}

/** Active nodes first (main, backup, witness), then revoked ones, newest first within each. */
export function sortNodes(nodes: readonly SiteNode[]): SiteNode[] {
  const rank = (n: SiteNode) => (n.status === 'revoked' ? 10 : 0) + { main: 0, backup: 1, witness: 2 }[n.role];
  return [...nodes].sort((a, b) => rank(a) - rank(b) || b.enrolledAt.localeCompare(a.enrolledAt));
}

/** A group's scope in words: "Whole tenant", or the sites and studios it names. */
export function scopeText(scope: Group['scope'], names: { readonly sites: ReadonlyMap<string, string>; readonly studios: ReadonlyMap<string, string> }): string {
  if (scope.kind === 'tenant') return 'Whole tenant';
  const parts = [...scope.siteIds.map((id) => `site ${names.sites.get(id) ?? id}`), ...scope.studioIds.map((id) => `studio ${names.studios.get(id) ?? id}`)];
  return parts.length === 0 ? 'Nothing (no site or studio chosen)' : parts.join(', ');
}

export type UsageByProduction = { readonly productionId: string; readonly runs: number; readonly hours: number; readonly costUsd: number };

/** The month's runs summed per production, the most expensive first. */
export function usageByProduction(usage: CloudUsageResponse): UsageByProduction[] {
  const byId = new Map<string, { runs: number; hours: number; costUsd: number }>();
  for (const run of usage.runs) {
    const sum = byId.get(run.productionId) ?? { runs: 0, hours: 0, costUsd: 0 };
    sum.runs += 1;
    sum.hours += run.hours;
    sum.costUsd += run.costUsd;
    byId.set(run.productionId, sum);
  }
  return [...byId.entries()].map(([productionId, sum]) => ({ productionId, ...sum })).sort((a, b) => b.costUsd - a.costUsd || a.productionId.localeCompare(b.productionId));
}

/** The last `count` months (UTC), newest first, as `YYYY-MM`. */
export function recentMonths(nowMs: number, count: number): string[] {
  const now = new Date(nowMs);
  const months: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    months.push(`${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return months;
}

export function usd(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export type AuditFilter = { readonly kind: AuditEntry['kind'] | 'all'; readonly outcome: AuditEntry['outcome'] | 'all'; readonly siteId: string | 'all'; readonly text: string };

export const NO_AUDIT_FILTER: AuditFilter = { kind: 'all', outcome: 'all', siteId: 'all', text: '' };

/** The entries the filter keeps; `text` matches the account, the source address or the node, case-insensitively. */
export function filterAudit(entries: readonly AuditEntry[], filter: AuditFilter): AuditEntry[] {
  const text = filter.text.trim().toLowerCase();
  return entries.filter(
    (e) =>
      (filter.kind === 'all' || e.kind === filter.kind) &&
      (filter.outcome === 'all' || e.outcome === filter.outcome) &&
      (filter.siteId === 'all' || e.siteId === filter.siteId) &&
      (text === '' || [e.account, e.sourceIp ?? '', e.nodeId].some((v) => v.toLowerCase().includes(text))),
  );
}

/** The device ids a studio config names (`config.devices`' keys), sorted; none for a config without them. */
export function deviceIdsOf(config: unknown): string[] {
  if (typeof config !== 'object' || config === null) return [];
  const devices = Reflect.get(config, 'devices');
  return typeof devices === 'object' && devices !== null && !Array.isArray(devices) ? Object.keys(devices).sort() : [];
}
